import React, { useState, useEffect, useCallback } from 'react';
import { assembliesAPI, ffeAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Package, Trash2, Pencil, Loader2, Copy, Layers } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['living', 'bedroom', 'kitchen', 'bath', 'dining', 'office', 'outdoor', 'lighting', 'other'];
const fmtINR = (n) => n ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

export default function Assemblies() {
  const [assemblies, setAssemblies] = useState([]);
  const [ffeItems, setFfeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const emptyForm = { name: '', description: '', category: 'living', default_markup: 30, items: [] };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [a, f] = await Promise.all([assembliesAPI.list(), ffeAPI.list({})]);
      setAssemblies(a.data); setFfeItems(f.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const computeTotals = (items) => {
    let trade = 0, client = 0;
    items.forEach(it => {
      const qty = Number(it.quantity) || 1;
      const t = Number(it.trade_price) || 0;
      const m = Number(it.markup_percentage) || 0;
      const c = Number(it.client_price) || t * (1 + m / 100);
      trade += t * qty; client += c * qty;
    });
    return { trade: Math.round(trade), client: Math.round(client) };
  };

  const addItem = () => {
    setForm(f => ({ ...f, items: [...f.items, {
      item_id: `i_${Date.now()}`, name: '', description: '', quantity: 1,
      trade_price: 0, markup_percentage: f.default_markup, client_price: 0,
    }]}));
  };

  const updateItem = (idx, field, val) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: val };
      if (field === 'trade_price' || field === 'markup_percentage') {
        const t = Number(field === 'trade_price' ? val : items[idx].trade_price) || 0;
        const m = Number(field === 'markup_percentage' ? val : items[idx].markup_percentage) || 0;
        items[idx].client_price = Math.round(t * (1 + m / 100));
      }
      return { ...f, items };
    });
  };

  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const pickFromFFE = (idx, ffeId) => {
    const ffe = ffeItems.find(x => x.ffe_id === ffeId);
    if (!ffe) return;
    setForm(f => {
      const items = [...f.items];
      items[idx] = {
        ...items[idx],
        name: ffe.name, description: ffe.description,
        trade_price: ffe.trade_price, markup_percentage: ffe.markup_percentage,
        client_price: ffe.client_price, ffe_id: ffe.ffe_id,
      };
      return { ...f, items };
    });
  };

  const openCreate = () => { setForm(emptyForm); setEditing(null); setDialogOpen(true); };
  const openEdit = (a) => { setForm({ ...emptyForm, ...a }); setEditing(a); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setFormLoading(true);
    try {
      if (editing) await assembliesAPI.update(editing.assembly_id, form);
      else await assembliesAPI.create(form);
      toast.success(editing ? 'Updated' : 'Created');
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try { await assembliesAPI.delete(a.assembly_id); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error('Failed'); }
  };

  const handleClone = async (a) => {
    try { await assembliesAPI.clone(a.assembly_id); toast.success('Cloned'); fetchData(); }
    catch (e) { toast.error('Failed'); }
  };

  const totals = computeTotals(form.items);

  return (
    <Layout>
      <div className="space-y-6" data-testid="assemblies-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3">
              <Layers className="h-7 w-7 text-amber-500" />Assemblies
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Bundled item groups — e.g. "Master Bedroom Suite" or "Standard Kitchen Package"</p>
          </div>
          <Button onClick={openCreate} data-testid="create-assembly-btn"><Plus className="h-4 w-4 mr-2" />New Assembly</Button>
        </div>

        {loading ? <div className="animate-pulse space-y-3">{[1,2].map(i => <div key={i} className="h-24 bg-muted/40 rounded-xl" />)}</div> :
          assemblies.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><Package className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No assemblies yet. Create grouped item bundles to speed up quoting.</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {assemblies.map(a => (
                <Card key={a.assembly_id} className="hover:shadow-md transition-all" data-testid={`assembly-${a.assembly_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium">{a.name}</div>
                        <div className="text-xs text-muted-foreground capitalize">{a.category}</div>
                      </div>
                      <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">{a.items?.length || 0} items</span>
                    </div>
                    {a.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{a.description}</p>}
                    <div className="flex items-center justify-between mt-3 text-sm">
                      <div><span className="text-muted-foreground">Trade:</span> {fmtINR(a.trade_total)}</div>
                      <div className="font-medium text-emerald-600">{fmtINR(a.client_total)}</div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button variant="outline" size="sm" onClick={() => openEdit(a)}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => handleClone(a)}><Copy className="h-3 w-3 mr-1" />Clone</Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(a)} className="text-red-600 ml-auto"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        }
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Assembly' : 'New Assembly'}</DialogTitle><DialogDescription>Group items together for quick reuse in proposals</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="assembly-name-input" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div><Label>Default Markup %</Label><Input type="number" value={form.default_markup} onChange={e => setForm({...form, default_markup: e.target.value})} /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="h-3 w-3 mr-1" />Add Item</Button>
              </div>
              {form.items.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 border-2 border-dashed rounded">No items yet</p> :
                <div className="space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={it.item_id || idx} className="p-3 border rounded-lg space-y-2 bg-muted/20">
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                        <Input className="sm:col-span-4" placeholder="Item name" value={it.name || ''} onChange={e => updateItem(idx, 'name', e.target.value)} />
                        <Select value={it.ffe_id || ''} onValueChange={v => pickFromFFE(idx, v)}>
                          <SelectTrigger className="sm:col-span-3"><SelectValue placeholder="Pick FF&E" /></SelectTrigger>
                          <SelectContent>{ffeItems.slice(0, 50).map(f => <SelectItem key={f.ffe_id} value={f.ffe_id}>{f.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className="sm:col-span-1" type="number" placeholder="Qty" value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                        <Input className="sm:col-span-2" type="number" placeholder="Trade" value={it.trade_price} onChange={e => updateItem(idx, 'trade_price', e.target.value)} />
                        <Input className="sm:col-span-1" type="number" placeholder="M%" value={it.markup_percentage} onChange={e => updateItem(idx, 'markup_percentage', e.target.value)} />
                        <Button type="button" variant="ghost" size="sm" className="sm:col-span-1 text-red-600" onClick={() => removeItem(idx)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                      <div className="text-xs text-muted-foreground">Client price: {fmtINR(it.client_price)}</div>
                    </div>
                  ))}
                </div>
              }
            </div>

            <div className="flex items-center justify-end gap-6 p-3 rounded bg-amber-50">
              <div className="text-sm"><span className="text-muted-foreground">Trade Total:</span> <span className="font-medium">{fmtINR(totals.trade)}</span></div>
              <div className="text-sm"><span className="text-muted-foreground">Client Total:</span> <span className="font-medium text-emerald-600">{fmtINR(totals.client)}</span></div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-assembly-btn">{formLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
